import { watchDebounced } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { watch } from "vue";

import {
  URL_PARAMETERS,
  type TURLParameterValues,
} from "../utils/urlParams.ts";

import { useUrlParameterStore } from "./paramStore.ts";
import {
  BUILTIN_LAYER_IDS,
  useGlobeControlStore,
  type TGlobeControlStoreKeys,
} from "./store.ts";

import { encodeVolumeUrlState } from "@/lib/volume/volumeUrlState.ts";

type TUrlSyncEntry = {
  key:
    | TGlobeControlStoreKeys
    | keyof ReturnType<typeof useUrlParameterStore>["$state"];
  param: TURLParameterValues;
  transform?: (v: unknown) => string | number;
  skip?: (v: unknown) => boolean;
};

const GLOBE_URL_SYNC_MAP: TUrlSyncEntry[] = [
  {
    key: "varnameSelector",
    param: URL_PARAMETERS.VARNAME,
    skip: (v) => !v || v === "-",
  },
  { key: "colormap", param: URL_PARAMETERS.COLORMAP },
  {
    key: "invertColormap",
    param: URL_PARAMETERS.INVERT_COLORMAP,
    transform: String,
  },
  {
    key: "posterizeLevels",
    param: URL_PARAMETERS.POSTERIZE_LEVELS,
    transform: String,
  },
  {
    key: "hideLowerBound",
    param: URL_PARAMETERS.HIDE_LOWER_BOUND,
    transform: String,
  },
  {
    key: "hideUpperBound",
    param: URL_PARAMETERS.HIDE_UPPER_BOUND,
    transform: String,
  },
  { key: "landSeaMaskChoice", param: URL_PARAMETERS.MASK_MODE },
  {
    key: "landSeaMaskUseTexture",
    param: URL_PARAMETERS.MASK_USE_TEXTURE,
    transform: String,
  },
  { key: "projectionMode", param: URL_PARAMETERS.PROJECTION },
  { key: "digestPercentile", param: URL_PARAMETERS.PERCENTILE },
  { key: "digestPercentileLow", param: URL_PARAMETERS.PERCENTILE_LOW },
  { key: "digestPercentileHigh", param: URL_PARAMETERS.PERCENTILE_HIGH },
  { key: "digestStratum", param: URL_PARAMETERS.STRATUM },
  {
    key: "gapFill",
    param: URL_PARAMETERS.SMOOTH,
    transform: (enabled) => (enabled ? "true" : ""),
  },
  { key: "gapFillSize", param: URL_PARAMETERS.SMOOTH_KERNEL },
  {
    key: "streamlineMagnitudeRequested",
    param: URL_PARAMETERS.STREAMLINE_MAGNITUDE,
    transform: (requested) => (requested ? "true" : ""),
  },
];

const URL_PARAM_SYNC_MAP: TUrlSyncEntry[] = [
  {
    key: "paramCameraPx",
    param: URL_PARAMETERS.CAMERA_PX,
  },
  {
    key: "paramCameraPy",
    param: URL_PARAMETERS.CAMERA_PY,
  },
  {
    key: "paramCameraAlt",
    param: URL_PARAMETERS.CAMERA_ALT,
  },
  {
    key: "paramGridType",
    param: URL_PARAMETERS.GRID_TYPE,
    transform: (v) => (v === undefined ? "" : String(v)),
  },
];

/* eslint-disable-next-line max-lines-per-function */
export function useUrlSync() {
  const store = useGlobeControlStore();
  const { userBoundsHigh, userBoundsLow, dimSlidersDisplay, projectionCenter } =
    storeToRefs(store);

  const urlParameterStore = useUrlParameterStore();

  function changeURLHash(
    entries: Partial<
      Record<TURLParameterValues | string, string | number | undefined>
    >
  ) {
    const [resource, ...paramArray] = location.hash.substring(1).split("::");
    const paramString = paramArray.join("&");
    const params = new URLSearchParams(paramString);

    for (const [key, value] of Object.entries(entries)) {
      if (value === undefined || value === "") {
        params.delete(key);
        continue;
      }
      params.set(key, value as string);
    }

    history.replaceState(
      null,
      "",
      document.location.pathname +
        "#" +
        resource +
        "::" +
        Object.entries(Object.fromEntries(params))
          .map(([k, v]) => `${k}=${v}`)
          .join("::")
    );
  }

  for (const { key, param, transform, skip } of GLOBE_URL_SYNC_MAP) {
    watch(
      () => (store as unknown as Record<string, unknown>)[key],
      (value) => {
        if (skip?.(value)) {
          return;
        }
        changeURLHash({
          [param]: transform ? transform(value) : (value as string | number),
        });
      }
    );
  }

  for (const { key, param, transform, skip } of URL_PARAM_SYNC_MAP) {
    watch(
      () => (urlParameterStore as unknown as Record<string, unknown>)[key],
      (value) => {
        if (skip?.(value)) {
          return;
        }
        changeURLHash({
          [param]: transform ? transform(value) : (value as string | number),
        });
      }
    );
  }

  watch(
    () => store.isStreamlineLayerEnabled(),
    (enabled) => {
      changeURLHash({
        [URL_PARAMETERS.STREAMLINES]: enabled ? "true" : "",
      });
    }
  );

  watch(
    () => store.isVolumeLayerEnabled(),
    (enabled) => {
      changeURLHash({
        [URL_PARAMETERS.VOLUME]: enabled ? "true" : "",
      });
    }
  );

  watchDebounced(
    () => encodeVolumeUrlState(store.volumeSelections),
    (state) => {
      changeURLHash({ [URL_PARAMETERS.VOLUME_STATE]: state });
    },
    { debounce: 100 }
  );

  watch(
    () =>
      store.layerStack.find((layer) => layer.id === BUILTIN_LAYER_IDS.VOLUME)
        ?.opacity,
    (opacity) => {
      changeURLHash({
        [URL_PARAMETERS.VOLUME_OPACITY]:
          typeof opacity === "number" && opacity < 1 ? opacity : "",
      });
    }
  );

  watch(
    () => [
      store.streamlineSelection.automatic,
      store.streamlineSelection.u,
      store.streamlineSelection.v,
    ],
    () => {
      const selection = store.streamlineSelection;
      changeURLHash({
        [URL_PARAMETERS.STREAMLINE_U]: selection.automatic
          ? ""
          : (selection.u ?? ""),
        [URL_PARAMETERS.STREAMLINE_V]: selection.automatic
          ? ""
          : (selection.v ?? ""),
      });
    }
  );

  // Debounced: user bounds
  watchDebounced(
    () => [userBoundsLow.value, userBoundsHigh.value],
    () => {
      const bothSet =
        userBoundsLow.value !== undefined && userBoundsHigh.value !== undefined;
      const bothUnset =
        userBoundsLow.value === undefined && userBoundsHigh.value === undefined;
      if (bothSet || bothUnset) {
        changeURLHash({
          [URL_PARAMETERS.USER_BOUNDS_LOW]: userBoundsLow.value as number,
          [URL_PARAMETERS.USER_BOUNDS_HIGH]: userBoundsHigh.value as number,
        });
      }
    },
    { debounce: 200 }
  );

  watchDebounced(
    () => [projectionCenter.value?.lat, projectionCenter.value?.lon],
    () => {
      const center = projectionCenter.value;
      if (!center) {
        return;
      }
      changeURLHash({
        [URL_PARAMETERS.LAT]: center.lat,
        [URL_PARAMETERS.LON]: center.lon,
      });
    },
    { debounce: 200 }
  );

  watch(
    () => dimSlidersDisplay.value,
    () => {
      const dimension = store.varinfo?.dimRanges;
      if (!dimension) {
        return;
      }
      const dimensionValues = {} as Record<string, string | number>;
      for (let i = 0; i < dimension.length; i++) {
        if (dimension[i] === null) {
          continue;
        }
        // In live mode the time index is driven by polling and re-seeded from
        // the server on load, so keep it out of the shareable URL.
        if (store.live && dimension[i]?.name === "time") {
          continue;
        }
        const val = store.dimSlidersDisplay[i];
        if (val !== null) {
          dimensionValues[`dimIndices_${dimension[i]?.name}` as string] = val;
        }
      }
      changeURLHash(dimensionValues);
    },
    { deep: true }
  );
}
